import {createAction, props} from '@ngrx/store';
import {IStore} from '../../../shared/shared-models/store/store';
import {FbQuery} from '../../../shared-utilities/models-old/firebase-queries';
import {
  StockItem,
  StockItemUpdateFieldValue,
  StockItemUpdateObj,
} from '../../../shared-utilities/models-old/datastructures';
import {IDisableStockItemRule} from '../../settings-new/models';
import {IFirebaseQuery} from '../../../shared/shared-models/firebase/firebase-queries';
import {
  AutoOrderEmailStatusType,
  AutoOrderIQStatusType,
  AutoOrderPDFStatusType,
  AutoOrderStatusType,
} from '../models/auto-order-order-status-types.enum';
import {AutoOrderQuantityWriteSourceEnum} from '../models/auto-order-quantity-write.enum';
import {
  IAutoOrderFilterOrderSettings,
  IAutoOrderListStatusChips,
  IAutoOrderOrder,
  IAutoOrderOrderItem,
  IAutoOrderOrderPreparedInfoDetails,
  IAutoOrderOrderPropertyPayload,
  IAutoOrderOrderStockItem,
  IAutoOrderSupplierEmails,
  IOpenAutoGeneratedOrder,
} from '../models';
import {IPaginationData} from '../../../shared/shared-models/pagination/pagination-data';
import {ISearchableFields} from '../../../shared/shared-models/type-sense/default-searchable-fields';
import {ISupplier} from '../../../shared/shared-models/stock/suppliers';
import {IFacetCounts} from '../../../shared/shared-models/type-sense/type-sense-types';
import {IGlobalError} from 'src/app/shared/shared-components/components/global-error-display/models/global-error';
import {IAutoOrderOrderingSettings, IAutoOrderExpirationSettings, TLineColour} from '../../settings-new/models';
import {AOScheduleElement} from '../models/old-models/ao-datastructures';

/**
 * STILL TO DO ON OPEN AUTO ORDER
 * IMPLEMENT THE LOADING BAR FOR OPENING AN ORDER
 */

//#region GLOBAL

/**
 * ### Set a property dynamically on the auto order state
 *
 * @deprecated Use specific typed actions instead (e.g., setSupplierEmailsLoading)
 * @action
 * @property {string} property - The name of the property to update on the state
 * @property {unknown} value - The value to assign to the property
 */
export const setPropertyOnAutoOrderState = createAction(
  '[AUTO ORDER][Global] Set Property on Auto Order State',
  props<{
    property: string;
    value: unknown;
  }>(),
);

/**
 * ### Set the supplier emails loading state
 *
 * @action
 * @property {boolean} isLoading - Whether supplier emails are currently loading
 */
export const setSupplierEmailsLoading = createAction(
  '[AUTO ORDER][Global] Set Supplier Emails Loading',
  props<{ isLoading: boolean }>(),
);

/**
 * ### Clear all auto ordering related state data
 *
 * @action
 */
export const clearAutoOrderingState = createAction(
  '[AUTO ORDER][Global] Clear Auto Order State',
);
//#endregion

//#region OPEN AUTO ORDER - GET AND INITIALIZE

/**
 * Open Auto Order
 *
 * Main Open Auto Order Flow:
 *
 * Notes on this:
 * - From step 3 onwards, this effect/action gets triggered for every chunk of 100 items.
 * - We do limit adding only 100 items to an order from the Front End. This is just precautionary.
 * - This means steps 3 through 10 will execute repeatedly for each chunk in the array of chunks.
 *
 * ```
 * DIAGRAM FLOW OF HOW AUTO ORDERS WORK
 *
 * [01] Open Auto Order
 *  │
 *  ↓
 * [02] Initialize Open Auto Order
 *  │
 *  ↓
 * [03][01] Get Order Items for Open Auto Order
 *  │
 *  ↓
 *  ├─── → [03][02] Get Order Items for Open Auto Order Success
 *  │       │   │
 *  │       ↓   └─── ← [USER ADDS ITEM TO ORDER]
 *  │       │
 *  │       └─── → [03][03] Get Order Items for Open Auto Order With Latest Progress
 *  │               │
 *  │               └─── → [06][01] Get Stock Items for Added Items to Order
 *  │                       │
 *  │                       └─── → [06][02] Get Stock Items for Added Items to Order Success
 *  │                               │   │
 *  │                               ↓   └─── ← [LIVE CHANGES ON FIRESTORE TO STOCK ITEM]
 *  │                               │
 *  │                               └─── → [06][03] Get Stock Items for Added Items to Order With Latest Progress
 *  │                                       │
 *  │                                       └─ → ─────────────────────────────────────────────────────────────── → ─┐
 *  ↓                                                                                                               │
 *  ├─── → [04][01] Get Stock Items for Order Regular Supplier                                                      │
 *  │       │                                                                                                       │
 *  │       └─── → [04][02] Get Stock Items for Order Regular Supplier Success                                      │
 *  │               │   │                                                                                           │
 *  │               ↓   └─── ← [LIVE CHANGES ON FIRESTORE TO STOCK ITEM]                                            │
 *  │               │                                                                                               │
 *  │               └─── → [04][03] Get Stock Items for Order Regular Supplier With Latest Progress                 │
 *  │                       │                                                                                       ↓
 *  │                       └─── → ───────────────────────────────────────────────────────────────────────────── → ─┤
 *  ↓                                                                                                               │
 *  └─── → [05][01] Get Stock Items for Order Additional Suppliers                                                  │
 *          │                                                                                                       │
 *          └─── → [05][02] Get Stock Items for Order Additional Suppliers Success                                  │
 *                  │   │                                                                                           │
 *                  ↓   └─── ← [LIVE CHANGES ON FIRESTORE TO STOCK ITEM]                                            │
 *                  │                                                                                               │
 *                  └─── → [05][03] Get Stock Items for Order Additional Suppliers Success With Latest Progress     │
 *                          │                                                                                       ↓
 *                          └─── → ───────────────────────────────────────────────────────────────────────────── → ─┤
 *                                                                                                                  │
 *                                                          Group Stock Items by Code for Open Auto Order [07] ← ───┘
 *                                                                                                         │
 *                                     Get Missing Stock Codes and Order Items on Open Order [08][01] ← ───┘
 *                                                                                                │
 *                    Get Missing Stock Codes and Order Items on Open Order Success [08][02] ← ───┤
 *                                                                                   │            │
 *                     Get Edited Order Stock Items from Local Storage [09][01] ← ───┘            │
 *                                                                        │                       ↓
 *  Get Edited Order Stock Items from Local Storage Success [09][02] ← ───┴───────────────────────┘
 *                                                           │
 *                         Open Auto Order Success [10] ← ───┘
 *
 * ```
 */


export const setOpenOrderLoadingIndicator = createAction(
  '[AUTO ORDER][Open Step...] Set Open Auto Order Loading Indicator',
  props<{ property: string; value: unknown }>(),
);

export const closeAutoOrder = createAction(
  '[AUTO ORDER][Open Step 00] Close Auto Order',
);

export const openAutoOrder = createAction(
  '[AUTO ORDER][Open Step 01] Open Auto Order',
  props<{ orderId: string }>(),
);

export const requestOpenAutoOrderFromNotification = createAction(
  '[AUTO ORDER][Notification] Request Open Auto Order From Notification',
  props<{ orderId: string; storeId: string; order: IAutoOrderOrder | null }>(),
);

export const clearPendingOpenAutoOrder = createAction(
  '[AUTO ORDER][Notification] Clear Pending Open Auto Order',
);

export const initializeOpenAutoOrder = createAction(
  '[AUTO ORDER][Open Step 02] Initialize Open Auto Order',
  props<{ progress: IOpenAutoGeneratedOrder }>(),
);

export const getOrderItemsForOpenAutoOrder = createAction(
  '[AUTO ORDER][Open Step 03][01] Get Order Items for Open Auto Order',
  props<{ progress: IOpenAutoGeneratedOrder }>(),
);

export const getOrderItemsForOpenAutoOrderSuccess = createAction(
  '[AUTO ORDER][Open Step 03][02] Get Order Items for Open Auto Order Success',
  props<{ progress: IOpenAutoGeneratedOrder }>(),
);

export const getOrderItemsForOpenAutoOrderWithLatestProgress = createAction(
  '[AUTO ORDER][Open Step 03][03] Get Order Items for Open Auto Order With Latest Progress',
  props<{ progress: IOpenAutoGeneratedOrder }>(),
);

export const getStockItemsForOrderRegularSupplier = createAction(
  '[AUTO ORDER][Open Step 04][01] Get Stock Items for Order Regular Supplier',
  props<{ progress: IOpenAutoGeneratedOrder; query: IFirebaseQuery[] }>(),
);

export const getStockItemsForOrderRegularSupplierSuccess = createAction(
  '[AUTO ORDER][Open Step 04][02] Get Stock Items for Order Regular Supplier Success',
  props<{ progress: IOpenAutoGeneratedOrder }>(),
);

export const getStockItemsForOrderRegularSupplierWithLatestProgress = createAction(
  '[AUTO ORDER][Open Step 04][03] Get Stock Items for Order Regular Supplier With Latest Progress',
  props<{ progress: IOpenAutoGeneratedOrder }>(),
);

export const getStockItemsForOrderAdditionalSuppliers = createAction(
  '[AUTO ORDER][Open Step 05][01] Get Stock Items for Order Additional Suppliers',
  props<{ progress: IOpenAutoGeneratedOrder; query: IFirebaseQuery[] }>(),
);

export const getStockItemsForOrderAdditionalSuppliersSuccess = createAction(
  '[AUTO ORDER][Open Step 05][02] Get Stock Items for Order Additional Suppliers Success',
  props<{ progress: IOpenAutoGeneratedOrder }>(),
);

export const getStockItemsForOrderAdditionalSuppliersWithLatestProgress = createAction(
  '[AUTO ORDER][Open Step 05][03] Get Stock Items for Order Additional Suppliers Success With Latest Progress',
  props<{ progress: IOpenAutoGeneratedOrder }>(),
);

export const getStockItemsForAddedItemsToOrder = createAction(
  '[AUTO ORDER][Open Step 06][01] Get Stock Items for Added Items to Order',
  props<{ progress: IOpenAutoGeneratedOrder; orderItemsAdded: IAutoOrderOrderItem[][] }>(),
);

export const getStockItemsForAddedItemsToOrderSuccess = createAction(
  '[AUTO ORDER][Open Step 06][02] Get Stock Items for Added Items to Order Success',
  props<{ progress: IOpenAutoGeneratedOrder }>(),
);

export const getStockItemsForAddedItemsToOrderWithLatestProgress = createAction(
  '[AUTO ORDER][Open Step 06][03] Get Stock Items for Added Items to Order With Latest Progress',
  props<{ progress: IOpenAutoGeneratedOrder }>(),
);

export const groupStockItemsByCodeForOpenAutoOrder = createAction(
  '[AUTO ORDER][Open Step 07] Group Stock Items by Code for Open Auto Order',
  props<{ progress: IOpenAutoGeneratedOrder }>(),
);

export const getMissingStockCodesAndOrderItemsOnOpenOrder = createAction(
  '[AUTO ORDER][Open Step 08][01] Get Missing Stock Codes and Order Items on Open Order',
  props<{ progress: IOpenAutoGeneratedOrder }>(),
);

export const getMissingStockCodesAndOrderItemsOnOpenOrderSuccess = createAction(
  '[AUTO ORDER][Open Step 08][02] Get Missing Stock Codes and Order Items on Open Order Success',
  props<{ progress: IOpenAutoGeneratedOrder }>(),
);

export const getEditedOrderStockItemsFromLocalStorage = createAction(
  '[AUTO ORDER][Open Step 09][01] Get Edited Order Stock Items from Local Storage',
  props<{ progress: IOpenAutoGeneratedOrder }>(),
);

export const getEditedOrderStockItemsFromLocalStorageSuccess = createAction(
  '[AUTO ORDER][Open Step 09][02] Get Edited Order Stock Items from Local Storage Success',
  props<{ progress: IOpenAutoGeneratedOrder; orderId: string }>(),
);

export const openAutoOrderSuccess = createAction(
  '[AUTO ORDER][Open Step 10] Open Auto Order Success',
  props<{ orderId: string; success: boolean }>(),
);

//#endregion

//#region OPEN AUTO ORDER - ADD ITEMS TO ORDER

/**
 * Action to add stock items to an open auto order.
 *
 * @property {string} orderId - The ID of the open order.
 * @property {IAutoOrderOrderStockItem[]} stockItems - The list of stock items to add.
 * @property {string} supplierId - The supplier's ID for these stock items.
 *
 * @example
 * dispatch(addItemsToOpenOrder({ orderId: '123', stockItems: [...], supplierId: 'abc' }));
 */
export const addItemsToOpenOrder = createAction(
  '[AUTO ORDER][Open Order][Add Items] Add Items To Open Order',
  props<{ orderId: string; stockItems: IAutoOrderOrderStockItem[]; supplierId: string }>(),
);

/**
 * Action dispatched when items have been successfully added to the open auto order.
 *
 * @example
 * dispatch(addItemsToOpenOrderSuccess());
 */
export const addItemsToOpenOrderSuccess = createAction(
  '[AUTO ORDER][Open Order][Add Items] Add Items To Open Order Success',
);

//#endregion

//#region OPEN AUTO ORDER - ADD STOCK TAGS TO STOCK ITEMS FROM OPEN AUTO ORDER
/**
 * Action dispatched to set stock tags for stock items during the
 * "Add Stock Item" step of the open auto order flow.
 *
 * @example
 * dispatch(setStockTagsForAddStockItem({ taggedItems: { '123': ['tag1', 'tag2'] } }));
 */
export const setStockTagsForAddStockItem = createAction(
  '[AUTO ORDER][Open Order][Add Stock Tags] Set Stock Tags For Add Stock Item',
  props<{ taggedItems: Record<string, string[]> }>(),
);

/**
 * Action dispatched when stock tags have been successfully set for the specified items.
 *
 * @example
 * dispatch(setStockTagsForAddStockItemSuccess());
 */
export const setStockTagsForAddStockItemSuccess = createAction(
  '[AUTO ORDER][Open Order][Add Stock Tags] Set Stock Tags For Add Stock Item Success',
);

/**
 * Action dispatched when setting stock tags fails due to an error.
 *
 * @example
 * dispatch(setStockTagsForAddStockItemFailure({ error }));
 */
export const setStockTagsForAddStockItemFailure = createAction(
  '[AUTO ORDER][Open Order][Add Stock Tags] Set Stock Tags For Add Stock Item Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region OPEN AUTO ORDER - REMOVE ITEMS FROM ORDER
/**
 * ### Action to remove items from an open auto order
 *
 * @action
 * @property {string} orderId - The ID of the order from which items should be removed
 * @property {string[]} codesToRemove - The list of stock item codes to be removed
 */
export const removeItemsFromOpenOrder = createAction(
  '[AUTO ORDER][Open Order] Remove Items From Open Order',
  props<{ orderId: string, codesToRemove: string[] }>(),
);

/**
 * ### Action dispatched after successfully removing items from an open auto order
 *
 * @action
 * @property {string} orderId - The ID of the order from which items were removed
 * @property {string[]} codesToRemove - The list of stock item codes that were removed
 */
export const removeItemsFromOpenOrderSuccess = createAction(
  '[AUTO ORDER][Open Order] Remove Items From Open Order Success',
  props<{ orderId: string, codesToRemove: string[] }>(),
);

//#endregion

//#region AUTO ORDER - UPDATE
/**
 * ### Action to set a specific property on an open auto order
 *
 * @action
 * @property {IAutoOrderOrderPropertyPayload<keyof IAutoOrderOrder>} property -
 * The payload containing the order ID, target field name (key of `IAutoOrderOrder`),
 * and the new value to set for that field.

 */
export const setPropertyOnAutoOrder = createAction(
  '[AUTO ORDER][Set Property] Set Property On Auto Order',
  props<{ property: IAutoOrderOrderPropertyPayload<keyof IAutoOrderOrder> }>(),
);

/**
 * ### Action dispatched after successfully updating a property on an open auto order
 *
 * @action
 * @description Used to confirm the success of a dynamic field update on an `IAutoOrderOrder`.
 *              This may trigger further state updates or UI feedback like a toast or refresh.
 *
 */
export const setPropertyOnAutoOrderSuccess = createAction(
  '[AUTO ORDER][Set Property] Set Property On Auto Order Success',
);
//#endregion

//#region AUTO ORDER - UPDATE ORDER PROPERTY ON STATE
export const updateStateAutoOrderOrder = createAction(
  '[AUTO ORDER][Update State Property] Update State Auto Order Order',
  props<{ orderIds: string[], store: IStore }>(),
);

//#endregion

//#region AUTO ORDER - SUBMIT
export const submitAutoOrders = createAction(
  '[AUTO ORDER][Submit Orders] Submit Auto Orders',
  props<{
    storeId: string;
    orderIds: string[];
    recipients: { [orderId: string]: string[] };
    skipEmails: { [orderId: string]: boolean };
  }>(),
);

export const submitAutoOrdersSuccess = createAction(
  '[AUTO ORDER][Submit Orders] Submit Auto Orders Success',
  props<{ orderIds: string[] }>(),
);

export const submitAutoOrdersFailure = createAction(
  '[AUTO ORDER][Submit Orders] Submit Auto Orders Failure',
  props<{ error: IGlobalError }>(),
);
//#endregion

// todo replace these also
export const setStockItemTagsForAddedOpenOrderItems = createAction(
  '[AUTO ORDER][Open Order][Add Stock Tags] Set Stock Item Tags For Added Open Order Items',
  props<{ taggedItems: Record<string, string[]> }>(),
);

export const setStockItemTagsForAddedOpenOrderItemsSuccess = createAction(
  '[AUTO ORDER][Open Order][Add Stock Tags] Set Stock Item Tags For Added Open Order Items Success',
  props<{ taggedItems: Record<string, string[]> }>(),
);

// ====================================================================================================
// Send Auto Order
// ====================================================================================================
export const setRetryAutoOrder = createAction(
  '[Auto Order][Orders] Set Retry Auto Order',
  props<{
    order: IAutoOrderOrder;
    skipRetryableFailureCheck?: boolean;
  }>(),
);

export const setRetryAutoOrderSuccess = createAction(
  '[Auto Order][Orders] Set Retry Auto Order Success',
);

export const syncPendingRetryAutoOrderFromFirestore = createAction(
  '[Auto Order][Orders] Sync Pending Retry Auto Order From Firestore',
  props<{ storeId: string; order: IAutoOrderOrder }>(),
);

export const setRetryAutoOrderFailure = createAction(
  '[Auto Order][Orders] Set Retry Auto Order Failure',
  props<{ error: IGlobalError }>(),
);

/**
 * Removes failed stock items from an order and retries submission.
 */
export const removeFailedItemsAndRetryAutoOrder = createAction(
  '[Auto Order][Orders] Remove Failed Items And Retry Auto Order',
  props<{
    storeId: string;
    orderId: string;
    codesToRemove: string[];
    order: IAutoOrderOrder;
  }>(),
);

export const updateAutoOrderStatusField = createAction(
  '[Auto Order][Email] Update Auto Order Order Status Field',
  props<{
    orderId: string;
    status: AutoOrderEmailStatusType | AutoOrderStatusType | AutoOrderPDFStatusType | AutoOrderIQStatusType,
    field: 'emailStatus' | 'iqStatus' | 'pdfStatus' | 'status' | 'errorMsg'
  }>(),
);

export const updateAutoOrderStatusFieldSuccess = createAction(
  '[Auto Order][Email] Update Auto Order Order Status Field Success',
);

export const updateAutoOrderStatusFieldFailure = createAction(
  '[Auto Order][Email] Update Auto Order Order Status Field Failure',
  props<{ error: IGlobalError }>(),
);


// ====================================================================================================
// Auto Order Orders
// ====================================================================================================
export const setAutoOrderListSortingOrder = createAction(
  '[Auto Order][Orders] Set Auto Orders List Sorting Direction',
  props<{ sortBy: { property: string; direction: 'asc' | 'desc' } }>(),
);

export const setAutoOrderSelectedOrders = createAction(
  '[Auto Order][Orders] Set Selected Auto Orders',
  props<{ orderIds: string[] }>(),
);

export const getAutoOrderOrders = createAction(
  '[Auto Order][Orders] Get Auto Order Orders',
  props<{ firebaseQueries: IFirebaseQuery[], statusChip: IAutoOrderListStatusChips }>(),
);

export const setAutoOrderChipStatus = createAction(
  '[Auto Order][Orders] Set Auto Order Chip Status',
  props<{ chips: IAutoOrderListStatusChips }>(),
);

export const getAutoOrderOrdersSuccess = createAction(
  '[Auto Order][Orders] Get Auto Order Orders Success',
  props<{
    autoOrderOrders: IAutoOrderOrder[],
    store: IStore,
    firebaseQueries: IFirebaseQuery[],
    statusChip: IAutoOrderListStatusChips
  }>(),
);

export const getAutoOrderOrdersFailure = createAction(
  '[Auto Order][Orders] Get Auto Order Orders Failure',
  props<{ error: IGlobalError }>(),
);

export const updateAutoOrderOrderEmailStatus = createAction(
  '[Auto Order][Email] Update Auto Order Order Email Status',
  props<{ orderId: string; status: AutoOrderEmailStatusType }>(),
);

export const updateAutoOrderOrderEmailRecipients = createAction(
  '[Auto Order][Email] Update Auto Order Order Email Recipients',
  props<{ orderId: string; recipients: string[] }>(),
);

export const updateAutoOrderOrderEmailRecipientsSuccess = createAction(
  '[Auto Order][Email] Update Auto Order Order Email Recipients Success',
);

export const updateAutoOrderOrderEmailRecipientsFailure = createAction(
  '[Auto Order][Email] Update Auto Order Order Email Recipients Failure',
  props<{ error: IGlobalError }>(),
);

export const sendAutoOrderEmails = createAction(
  '[Auto Order][Email] Send Auto Order Emails',
  props<{ order: IAutoOrderOrder }>(),
);

export const sendAutoOrderEmailsSuccess = createAction(
  '[Auto Order][Email] Send Auto Order Emails Success',
);

export const sendAutoOrderEmailsFailure = createAction(
  '[Auto Order][Email] Send Auto Order Emails Failure',
  props<{ error: IGlobalError }>(),
);

export const setAutoOrderingOrderFilter = createAction(
  '[Auto Order][Order] Set Auto Ordering Order Filter',
  props<{ filter: IAutoOrderFilterOrderSettings }>(),
);

//#region AUTO ORDER - UPDATE ITEM PROPERTY
export const updateItemPropertyOnAutoOrder = createAction(
  '[AUTO ORDER][Order Item] Update Item Property On Auto Order',
  props<{ stockCode: string, property: string, newValue: number, updateReducer: boolean }>(),
);

export const updateItemPropertyOnAutoOrderSuccess = createAction(
  '[AUTO ORDER][Order Item] Update Item Property On Auto Order Success',
  props<{ stockCode: string, property: string, newValue: number, selectedOrderId: string, updateReducer: boolean }>(),
);


// ====================================================================================================
// Delete Auto Orders
// ====================================================================================================
export const deleteAutoOrderOrder = createAction(
  '[Auto Order][Delete] Delete Auto Order Order',
  props<{ orderIds: string[], doNotAskAgain: boolean }>(),
);

export const deleteAutoOrderOrderWithStoreId = createAction(
  '[Auto Order][Delete] Delete Auto Order Order With Store Id',
  props<{ orderIds: string[], store: IStore }>(),
);

export const deleteAutoOrderOrderSuccess = createAction(
  '[Auto Order][Delete] Delete Auto Order Order Success',
  props<{ orderIds: string[], store: IStore }>(),
);

export const deleteAutoOrderOrderFailure = createAction(
  '[Auto Order][Delete] Delete Auto Order Order Failure',
  props<{ error: IGlobalError }>(),
);

// ====================================================================================================
// Schedule Auto Delete Auto Orders Settings
// ====================================================================================================
export const getAutoOrderAutoDeleteSettings = createAction(
  '[Auto Order][Auto Delete] Get Auto Order Auto Delete Settings',
);

export const getAutoOrderAutoDeleteSettingsSuccess = createAction(
  '[Auto Order][Auto Delete] Get Auto Order Auto Delete Settings Success',
  props<{ settings: IAutoOrderExpirationSettings }>(),
);

export const getAutoOrderAutoDeleteSettingsFailure = createAction(
  '[Auto Order][Auto Delete] Get Auto Order Auto Delete Settings Failure',
  props<{ error: IGlobalError }>(),
);

export const setAutoOrderAutoDeleteSettings = createAction(
  '[Auto Order][Auto Delete] Set Auto Order Auto Delete Settings',
  props<{ settings: IAutoOrderExpirationSettings }>(),
);

export const setAutoOrderAutoDeleteSettingsSuccess = createAction(
  '[Auto Order][Auto Delete] Set Auto Order Auto Delete Settings Success',
);

export const setAutoOrderAutoDeleteSettingsFailure = createAction(
  '[Auto Order][Auto Delete] Set Auto Order Auto Delete Settings Failure',
  props<{ error: IGlobalError }>(),
);

// ====================================================================================================
// Get Auto Order Stock Items Using Queries
// ====================================================================================================
export const getAutoOrderStockItems = createAction(
  '[Auto Order][Stock Items] Get Auto Order Stock Items',
  props<{ query: FbQuery[], orderId: string, stream: string }>(),
);

export const getAutoOrderStockItemsWithUserSelectedStore = createAction(
  '[Auto Order][Stock Items] Get Auto Order Stock Items with User Selected Store',
  props<{ query: FbQuery[], store: IStore, orderId: string, stream: string }>(),
);

export const getAutoOrderStockItemsWithUserSelectedStoreSuccess = createAction(
  '[Auto Order][Stock Items] Get Auto Order Stock Items with User Selected Store Success',
  props<{ stockItems: StockItem[], store: IStore, orderId: string, stream: string }>(),
);

export const getAutoOrderStockItemsWithUserSelectedStoreFailure = createAction(
  '[Auto Order][Stock Items] Get Auto Order Stock Items with User Selected Store Failure',
  props<{ error: IGlobalError, store: IStore, orderId: string, stream: string }>(),
);

// ====================================================================================================
// Stock Item Deletion Rules
// ====================================================================================================
export const getAutoOrderStockItemDeletionRules = createAction(
  '[Auto Order][Deletion Rules] Get Auto Order Stock Item Deletion Rules',
);

export const getAutoOrderStockItemDeletionRulesSuccess = createAction(
  '[Auto Order][Deletion Rules] Get Auto Order Stock Item Deletion Rules Success',
  props<{ store: IStore, disabledRules: IDisableStockItemRule }>(),
);

export const getAutoOrderStockItemDeletionRulesFailure = createAction(
  '[Auto Order][Deletion Rules] Get Auto Order Stock Item Deletion Rules Failure',
  props<{ error: IGlobalError, store: IStore }>(),
);

// ====================================================================================================
// Recipients and Suppliers
// ====================================================================================================

//#region Auto Order - Clear Supplier Emails
export const clearAllAutoOrderSupplierEmails = createAction(
  '[AUTO ORDER][Supplier Emails] Clear Supplier Emails',
);

//#endregion

//#region Auto Order - Get Supplier Emails

export const getSpecifiedSupplierEmailsForSelectedStore = createAction(
  '[AUTO ORDER][Supplier Emails] Get Specified Supplier Emails For Selected Store',
  props<{ supplierIds: string[], displayModal?: boolean, order?: IAutoOrderOrder, storeId?: string }>(),
);

export const getSpecifiedSupplierEmailsForSelectedStoreSuccess = createAction(
  '[AUTO ORDER][Supplier Emails] Get Specified Supplier Emails For Selected Store Success',
  props<{ emails: IAutoOrderSupplierEmails[], storeId: string }>(),
);
//#endregion

//#region Auto Order - Set Supplier Emails

export const setSupplierEmailsForSelectedStore = createAction(
  '[AUTO ORDER][Supplier Emails] Set Supplier Emails For Selected Store',
  props<{
    store: IStore,
    supplierId: string,
    additionalRecipients?: string[],
    selectedRecipients?: string[],
  }>(),
);

export const setSupplierEmailsForSelectedStoreSuccess = createAction(
  '[AUTO ORDER][Supplier Emails] Set Supplier Emails For Selected Store Success',
  props<{
    store: IStore,
    supplierId: string,
    additionalRecipients?: string[],
    selectedRecipients?: string[],
  }>(),
);

//#endregion

export const setAutoOrderSupplierAddRecipientEmails = createAction(
  '[Auto Order][Recipients] Set Auto Order Supplier Additional Recipient Emails',
  props<{ store: IStore, supplierId: string, recipientEmails: string[] }>(),
);

export const setAutoOrderSupplierAddRecipientEmailsSuccess = createAction(
  '[Auto Order][Recipients] Set Auto Order Supplier Additional Recipient Emails Success',
);

export const setAutoOrderSupplierAddRecipientEmailsFailure = createAction(
  '[Auto Order][Recipients] Set Auto Order Supplier Additional Recipient Emails Failure',
  props<{ error: IGlobalError }>(),
);

export const setAutoOrderSupplierEmails = createAction(
  '[Auto Order][Recipients] Set Auto Order Supplier Emails',
  props<{ store: IStore, supplierId: string, recipientEmails: string[], typeOfEmail: 'selected' | 'additional' }>(),
);

export const setAutoOrderSupplierEmailsWithSelectedUserStore = createAction(
  '[Auto Order][Recipients] Set Auto Order Supplier Emails With Selected User Store',
  props<{ supplierId: string, recipientEmails: string[], typeOfEmail: 'selected' | 'additional' }>(),
);

export const setAutoOrderSupplierEmailsSuccess = createAction(
  '[Auto Order][Recipients] Set Auto Order Supplier Emails Success',
);

export const setAutoOrderSupplierEmailsFailure = createAction(
  '[Auto Order][Recipients] Set Auto Order Supplier Emails Failure',
  props<{ error: IGlobalError }>(),
);

export const deleteAutoOrderSupplierEmails = createAction(
  '[Auto Order][Recipients] Delete Auto Order Supplier Emails',
  props<{ store: IStore, supplierId: string }>(),
);

export const deleteAutoOrderSupplierEmailsSuccess = createAction(
  '[Auto Order][Recipients] Delete Auto Order Supplier Emails Success',
);

export const deleteAutoOrderSupplierEmailsFailure = createAction(
  '[Auto Order][Recipients] Delete Auto Order Supplier Emails Failure',
  props<{ error: IGlobalError }>(),
);

// ====================================================================================================
// Auto Order Settings
// ====================================================================================================
export const getAutoOrderOrderSettings = createAction(
  '[Auto Order][Order Settings] Get Auto Order Order Settings',
);

export const getAutoOrderOrderSettingsSuccess = createAction(
  '[Auto Order][Order Settings] Get Auto Order Order Settings Success',
  props<{ settings: IAutoOrderOrderingSettings; storeId: string }>(),
);

export const getAutoOrderOrderSettingsFailure = createAction(
  '[Auto Order][Order Settings] Get Auto Order Order Settings Failure',
  props<{ error: IGlobalError }>(),
);

// ====================================================================================================
// Add Stock Items
// ====================================================================================================
export const getTypesenseStockItems = createAction(
  '[Auto Order][Add Item] Get Typesense Stock Items',
);

export const getTypesenseStockItemsSuccess = createAction(
  '[Auto Order][Add Item] Get Typesense Stock Items Success',
  props<{ stockItems: StockItem[]; paginationData: IPaginationData }>(),
);

export const getTypesenseStockItemsFailure = createAction(
  '[Auto Order][Add Item] Get Typesense Stock Items Failure',
  props<{ error: IGlobalError }>(),
);

export const getTypesenseFacetCounts = createAction(
  '[Auto Order][Edit Schedule] Get Typesense Facet Counts',
);

export const getTypesenseFacetCountsSuccess = createAction(
  '[Auto Order][Edit Schedule] Get Typesense Facet Counts Success',
  props<{ facetCounts: IFacetCounts }>(),
);

export const getTypesenseFacetCountsFailure = createAction(
  '[Auto Order][Edit Schedule] Get Typesense Facet Counts Failure',
  props<{ error: IGlobalError }>(),
);

export const getTypesenseStockItemsSearchResults = createAction(
  '[Auto Order][Add Item] Get Typesense Stock Items Search Results',
  props<{ idField: string, filters?: Record<string, ISearchableFields> }>(),
);

export const getTypesenseStockItemsSearchResultsSuccess = createAction(
  '[Auto Order][Add Item] Get Typesense Stock Items Search Results Success',
  props<{
    stockItems: StockItem[];
    paginationData: IPaginationData,
    filters: Record<string, ISearchableFields>,
    facetCounts: IFacetCounts
  }>(),
);

export const getTypesenseStockItemsSearchResultsFailure = createAction(
  '[Auto Order][Add Item] Get Typesense Stock Items Search Results Failure',
  props<{ error: IGlobalError }>(),
);

export const getAddItemLineColours = createAction(
  '[Auto Order][Add Item] Get Add Item Line Colours',
  props<{ pageStoreDocument: string }>(),
);

export const getAddItemLineColoursSuccess = createAction(
  '[Auto Order][Add Item] Get Add Item Line Colours Success',
  props<{ lineColours: TLineColour }>(),
);

export const getAddItemLineColoursFailure = createAction(
  '[Auto Order][Add Item] Get Add Item Line Colours Failure',
  props<{ error: IGlobalError }>(),
);

// =======================================================================
// Pagination
// =======================================================================
export const getPaginationResultsForAddStockItem = createAction(
  '[Auto Order][Add Item][Pagination] Get Pagination Results Add For Stock Item [Grid]',
  props<{ pageNumber: number }>(),
);

export const getPaginationResultsForAddStockItemSuccess = createAction(
  '[Auto Order][Add Item][Pagination] Get Pagination Results For Add Stock Items Success [Grid]',
  props<{ stockItems: StockItem[]; paginationData: IPaginationData }>(),
);

export const getPaginationResultsForAddStockItemFailure = createAction(
  '[Auto Order][Add Item][Pagination] Get Pagination Results For Add Stock Items Failure [Grid]',
  props<{ error: IGlobalError }>(),
);

export const setStockSearchAppliedFilter = createAction(
  '[Auto Order][Add Item] Set Filter For Stock Search',
  props<{ filter: Record<string, ISearchableFields> }>(),
);

export const setCustomOrder = createAction(
  '[Auto Order][Custom Order] Set Custom Order',
  props<{ supplierData: ISupplier, stockItemData: StockItem[] }>(),
);

export const setCustomOrderSuccess = createAction(
  '[Auto Order][Custom Order] Set Custom Order Success',
  props<{ supplierData: ISupplier, stockItemData: StockItem[] }>(),
);


export const setCustomOrderFailure = createAction(
  '[Auto Order][Custom Order] Set Custom Order Failure',
  props<{ error: IGlobalError }>(),
);

// ========================================================================
// NEW ACTIONS - Component Firebase Calls Migration
// ========================================================================

export const prepareAutoOrderForSubmission = createAction(
  '[Auto Order][Order Grid] Prepare Auto Order For Submission',
  props<{
    actionSource: AutoOrderQuantityWriteSourceEnum;
    storeId: string;
    order: IAutoOrderOrder;
    preparedItems: Record<string, IAutoOrderOrderPreparedInfoDetails>;
    priceEditsSnapshot: Record<string, number>;
    quantitiesSnapshot: Record<string, number>;
    total: number;
    status: string;
    comment?: string;
    dirtyCodes?: string[]
  }>(),
);

export const prepareAutoOrderForSubmissionSuccess = createAction(
  '[Auto Order][Order Grid] Prepare Auto Order For Submission Success',
  props<{
    orderId: string;
    priceEditsSnapshot: Record<string, number>;
    quantitiesSnapshot: Record<string, number>;
    storeId: string;
  }>(),
);

export const prepareAutoOrderForSubmissionFailure = createAction(
  '[Auto Order][Order Grid] Prepare Auto Order For Submission Failure',
  props<{ error: IGlobalError; orderId: string }>(),
);

export const clearPrepareAutoOrderOutcome = createAction(
  '[Auto Order][Order Grid] Clear Prepare Auto Order Outcome',
);

export const updateStockItemOnHold = createAction(
  '[Auto Order][Order Grid] Update Stock Item On Hold',
  props<{ storeId: string; updates: Record<string, StockItemUpdateObj> }>(),
);

export const updateStockItemOnHoldSuccess = createAction(
  '[Auto Order][Order Grid] Update Stock Item On Hold Success',
);

export const updateStockItemOnHoldFailure = createAction(
  '[Auto Order][Order Grid] Update Stock Item On Hold Failure',
  props<{ error: IGlobalError }>(),
);

export const setStockItemDisabledFromOrder = createAction(
  '[Auto Order][Order Grid] Set Stock Item Disabled From Order',
  props<{ enable: boolean; item: StockItem; storeId: string }>(),
);

export const setStockItemDisabledFromOrderSuccess = createAction(
  '[Auto Order][Order Grid] Set Stock Item Disabled From Order Success',
  props<{
    code: string;
    enable: boolean;
    fieldUpdates?: Record<string, StockItemUpdateFieldValue>;
    previousValues?: Record<string, StockItemUpdateFieldValue>;
  }>(),
);

export const setStockItemDisabledFromOrderFailure = createAction(
  '[Auto Order][Order Grid] Set Stock Item Disabled From Order Failure',
  props<{ error: IGlobalError }>(),
);

export const clearStockItemDisabledFromOrderResult = createAction(
  '[Auto Order][Order Grid] Clear Stock Item Disabled From Order Result',
);

export const updateStockItemFlag = createAction(
  '[Auto Order][Order Grid] Update Stock Item Flag',
  props<{ storeId: string; stockId: string; flagged: boolean }>(),
);

export const updateStockItemFlagSuccess = createAction(
  '[Auto Order][Order Grid] Update Stock Item Flag Success',
);

export const updateStockItemFlagFailure = createAction(
  '[Auto Order][Order Grid] Update Stock Item Flag Failure',
  props<{ error: IGlobalError }>(),
);

export const getAutoOrderingScheduleSettings = createAction(
  '[Auto Order][Edit Schedule] Get Auto Ordering Schedule Settings',
  props<{ storeId: string }>(),
);

export const getAutoOrderingScheduleSettingsSuccess = createAction(
  '[Auto Order][Edit Schedule] Get Auto Ordering Schedule Settings Success',
  props<{ storeId: string; dayCount: number }>(),
);

export const getAutoOrderingScheduleSettingsFailure = createAction(
  '[Auto Order][Edit Schedule] Get Auto Ordering Schedule Settings Failure',
  props<{ error: IGlobalError }>(),
);

export const cleanupAutoOrderingScheduleSettings = createAction(
  '[Auto Order][Edit Schedule] Cleanup Auto Ordering Schedule Settings',
  props<{ storeId: string }>(),
);

export const updateScheduleDayData = createAction(
  '[Auto Order][Edit Schedule] Update Schedule Day Data',
  props<{ storeId: string; day: string; scheduleData: Record<string, AOScheduleElement> }>(),
);

export const updateAutoOrderingScheduleSettings = createAction(
  '[Auto Order][Edit Schedule] Update Auto Ordering Schedule Settings',
  props<{ storeId: string; updates: Record<string, Record<string, AOScheduleElement | null>> }>(),
);

export const updateAutoOrderingScheduleSettingsSuccess = createAction(
  '[Auto Order][Edit Schedule] Update Auto Ordering Schedule Settings Success',
);

export const updateAutoOrderingScheduleSettingsFailure = createAction(
  '[Auto Order][Edit Schedule] Update Auto Ordering Schedule Settings Failure',
  props<{ error: IGlobalError }>(),
);

export const getAutoOrderingScheduleForDay = createAction(
  '[Auto Order][Unscheduled] Get Auto Ordering Schedule For Day',
  props<{ storeId: string; day: string }>(),
);

export const getAutoOrderingScheduleForDaySuccess = createAction(
  '[Auto Order][Unscheduled] Get Auto Ordering Schedule For Day Success',
  props<{ storeId: string; day: string; scheduleDay: Record<string, AOScheduleElement> }>(),
);

export const getAutoOrderingScheduleForDayFailure = createAction(
  '[Auto Order][Unscheduled] Get Auto Ordering Schedule For Day Failure',
  props<{ error: IGlobalError }>(),
);

export const requestUnscheduledAutoOrders = createAction(
  '[Auto Order][Unscheduled] Request Unscheduled Auto Orders',
  props<{ storeId: string; orderData: Record<string, AOScheduleElement> }>(),
);

export const requestUnscheduledAutoOrdersSuccess = createAction(
  '[Auto Order][Unscheduled] Request Unscheduled Auto Orders Success',
);

export const requestUnscheduledAutoOrdersFailure = createAction(
  '[Auto Order][Unscheduled] Request Unscheduled Auto Orders Failure',
  props<{ error: IGlobalError }>(),
);

export const setAutoOrderOrderSettings = createAction(
  '[Auto Order][Order Settings] Set Auto Order Order Settings',
  props<{ userId: string; storeId: string; settings: IAutoOrderOrderingSettings }>(),
);

export const setAutoOrderOrderSettingsSuccess = createAction(
  '[Auto Order][Order Settings] Set Auto Order Order Settings Success',
);

export const setAutoOrderOrderSettingsFailure = createAction(
  '[Auto Order][Order Settings] Set Auto Order Order Settings Failure',
  props<{ error: IGlobalError }>(),
);

// ====================================================================================================
// Schedule Loading Orchestration Actions
// ====================================================================================================

export const loadScheduleForStore = createAction(
  '[Auto Order][Edit Schedule] Load Schedule For Store',
  props<{ storeId: string }>(),
);

export const scheduleDataLoadingComplete = createAction(
  '[Auto Order][Edit Schedule] Schedule Data Loading Complete',
  props<{ storeId: string }>(),
);


export const scheduleDataLoadingFailed = createAction(
  '[Auto Order][Edit Schedule] Schedule Data Loading Failed',
  props<{ storeId: string; error: IGlobalError }>(),
);

export const setScheduleLoadingProgress = createAction(
  '[Auto Order][Edit Schedule] Set Schedule Loading Progress',
  props<{
    storeId: string;
    daysLoaded: string[];
    suppliersLoaded: boolean;
    expectedDayCount: number;
  }>(),
);

export const createStockTakeFromUncountedOrderItems = createAction(
  '[Auto Order][Open Order] Create Stock Take From Uncounted Items',
  props<{
    name: string;
    userAssigned: string;
    binLocation: string;
    includeDelCol: boolean;
    codes: string[];
  }>(),
);

export const createStockTakeFromUncountedOrderItemsSuccess = createAction(
  '[Auto Order][Open Order] Create Stock Take From Uncounted Items Success',
  props<{stockTakeId: string}>(),
);

export const createStockTakeFromUncountedOrderItemsFailure = createAction(
  '[Auto Order][Open Order] Create Stock Take From Uncounted Items Failure',
  props<{error: IGlobalError}>(),
);
